const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/mocktest'

/**
 * The SECOND same-origin API prefix: the Edvantage staff API (`api/urls.py`),
 * which is a different Django app from the mock-test one and is mounted at
 * `/api/` rather than `/mocktest/`.
 *
 * Only one capability lives here, and it is the reason the prefix exists at
 * all: employee (staff) accounts. `mocktest/urls.py` registers exactly one
 * employee route — `GET admin/admins` — so create, group assignment, enable,
 * disable and activation email are only reachable on this base
 * (`api/views/employee.py`).
 *
 * It is the same origin, the same nginx container and the same `tn` access
 * cookie: `api/authenticate.py::get_raw_jwt_access_token_from_cookies` reads
 * `tn` for every path outside `/mocktest/`, and `/mocktest/admin/auth/login/`
 * is literally `api.views.employee_auth.Login`, which sets `tn` at path `/`.
 * So an employee already signed in to the admin app is already authenticated
 * here; nothing new is stored and no second login exists.
 */
const STAFF_API_BASE_URL = import.meta.env.VITE_STAFF_API_BASE_URL || '/api'

/**
 * Which same-origin API prefix a request is addressed to. Defaults to the
 * mock-test API, so every existing caller keeps its current target and adding
 * the staff base changes nothing for them.
 */
export type ApiBase = 'mocktest' | 'staff'

const ROLE_KEY = 'ielts-v2-session-role'
const AUTH_TOKEN_KEY = 'authToken'
const REFRESH_TOKEN_KEY = 'refreshToken'
const CURRENT_USER_KEY = 'currentUser'
let refreshPromise: Promise<SessionRefreshOutcome> | null = null

/**
 * What a refresh attempt actually established. A boolean was not enough.
 *
 * `unauthenticated` means the server said so - the session is over and the
 * app must stop asking. `unavailable` means we could not find out: a network
 * failure, or a 5xx. Those two must never be treated alike. `api/exceptions.py`
 * deliberately answers 503 rather than 401 when Cloud Run exhausts its Cloud
 * SQL connections, with a comment saying it exists "so the frontend retries
 * instead of tearing down the session and sending the user to the login
 * screen". Collapsing both into `false` would sign a student out mid-exam
 * because a database pool filled up.
 */
export type SessionRefreshOutcome = 'refreshed' | 'unauthenticated' | 'unavailable'

type SessionExpiryListener = () => void
const sessionExpiryListeners = new Set<SessionExpiryListener>()
/** One sign-out per dead session, however many requests discover it at once. */
let sessionExpiryAnnounced = false

/**
 * Subscribe to "the server says this session is over".
 *
 * Returns an unsubscribe function. The auth context is the intended (single)
 * subscriber; it clears React state, empties the query cache and lets the
 * router send the student to the sign-in page.
 */
export function onSessionExpired(listener: SessionExpiryListener) {
  sessionExpiryListeners.add(listener)
  return () => {
    sessionExpiryListeners.delete(listener)
  }
}

/**
 * End the session exactly once.
 *
 * Before this existed a failed refresh only threw: auth state stayed put, so
 * every polling query kept its interval, `retry` doubled each failure and the
 * realtime bridge kept reconnecting - and each reconnect invalidated four
 * query families, re-firing them all. That feedback loop is what put ~800-900
 * requests an hour against five student endpoints on 2026-08-23, with 30
 * refresh 401s inside the window.
 */
function announceSessionExpired() {
  if (sessionExpiryAnnounced) return
  sessionExpiryAnnounced = true
  clearStoredSession()
  for (const listener of [...sessionExpiryListeners]) {
    // One throwing subscriber must not stop the others from tearing down.
    try {
      listener()
    } catch {
      /* ignore */
    }
  }
}

export type ApiErrorKind = 'http' | 'network' | 'unavailable' | 'invalid-response' | 'timeout'

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly kind: ApiErrorKind = 'http',
    options?: ErrorOptions,
    /**
     * The `data` object of a FAILED backend envelope. Django answers several
     * contract violations with a non-2xx status whose body still carries the
     * value the client needs to recover - the speaking processing notice is
     * the load-bearing case: 409 and 428 both return
     * `data.processing_notice_version`, and that response is the only runtime
     * source of the current version. Without this the payload was parsed and
     * thrown away.
     */
    public readonly data?: unknown,
  ) {
    super(message, options)
    this.name = 'ApiError'
  }
}

interface BackendEnvelope<T> {
  /** Newer clients may receive this, but the current Django envelope omits it. */
  success?: boolean
  /** Legacy Django responses include the HTTP status again in the JSON body. */
  status?: number
  data?: T
  message?: string
  error?: unknown
}

interface RefreshResponse {
  /** Mock-test refresh response. This is the refresh JWT used by the legacy WebSocket. */
  auth_token?: string
  /** Employee refresh response. */
  access_token?: string
  refresh_token?: string
}

interface StoredTokens {
  authToken: string
  refreshToken?: string
}

function errorText(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value)) return value.map(errorText).filter(Boolean).join(' ')
  return ''
}

function unavailableMessage() {
  return 'We could not reach Edvantage just now. Check your connection and try again.'
}

function isAuthEndpoint(endpoint: string) {
  return /^\/(?:admin\/)?auth(?:\/|$)/.test(endpoint)
}

/** Read one cookie by name; csrftoken is deliberately NOT HttpOnly so the
 *  double-submit pattern can echo it back in a header. */
function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`))
  if (!match) return null
  try {
    return decodeURIComponent(match.slice(name.length + 1))
  } catch {
    return null
  }
}

function shouldAttachStoredBearer(endpoint: string) {
  if (isAuthEndpoint(endpoint)) return false

  // Student login returns the refresh JWT as `auth_token` for the legacy
  // WebSocket contract. HTTP requests must authenticate with the HttpOnly
  // access cookie; sending that refresh JWT as a Bearer token makes Django
  // reject the request before it can fall back to the valid cookie.
  return localStorage.getItem(ROLE_KEY) === 'admin'
}

function persistTokens(tokens: Partial<RefreshResponse>) {
  const authToken = tokens.access_token || tokens.auth_token
  if (authToken) localStorage.setItem(AUTH_TOKEN_KEY, authToken)

  const refreshToken = tokens.refresh_token || tokens.auth_token
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

function refreshPath() {
  return localStorage.getItem(ROLE_KEY) === 'admin' ? '/admin/auth/refresh/' : '/auth/refresh/'
}

/**
 * Always refreshed on the MOCK-TEST base, including for a staff-base request.
 *
 * `/mocktest/admin/auth/refresh/` and `/api/employee/token/refresh` are the same
 * view (`employee_auth.RefreshToken`) writing the same `tn`/`rtn` cookie pair at
 * path `/`, so one refresh re-authenticates both prefixes. Refreshing on the
 * base that happened to 401 would fork this into two code paths that can drift
 * without either one failing visibly.
 */
async function refreshSession(): Promise<SessionRefreshOutcome> {
  // Concurrent 401s share one attempt. Five student queries failing together
  // must produce one refresh, not five.
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${refreshPath()}`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      // The refresh endpoint answers 401 for a missing, expired, revoked or
      // undecodable refresh cookie, and 403 for a suspended or deactivated
      // account. Both mean the same thing to the client: this session is over.
      if (response.status === 401 || response.status === 403) return 'unauthenticated'
      // Anything else that is not a success - 5xx, the deliberate 503, a proxy
      // error page - is "we could not find out". Keep the session.
      if (!response.ok) return 'unavailable'

      const envelope = await parseEnvelope<RefreshResponse>(response)
      if (envelope.success === false) return 'unauthenticated'
      persistTokens(envelope.data || {})
      return 'refreshed'
    } catch {
      return 'unavailable'
    }
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

async function parseEnvelope<T>(response: Response): Promise<BackendEnvelope<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    if (response.status >= 500) {
      throw new ApiError(unavailableMessage(), response.status, 'unavailable')
    }
    throw new ApiError(
      `Something went wrong at our end (${response.status}). Try again in a moment.`,
      response.status,
      'invalid-response',
    )
  }

  try {
    return (await response.json()) as BackendEnvelope<T>
  } catch (cause) {
    throw new ApiError(
      `Edvantage sent something we could not read (${response.status}). Try again in a moment.`,
      response.status,
      'invalid-response',
      { cause },
    )
  }
}

/**
 * `RequestInit` plus the one option the admin surfaces need.
 *
 * There is no global default timeout, deliberately. Adding one would silently
 * change every existing caller - including the runner - so the deadline is
 * always an explicit per-endpoint decision, exactly as the Vue client made it
 * (`timeoutMs: 120000` on the admin result lists, whose ORM aggregation and
 * serialization routinely outran a 30-second abort and surfaced as an
 * `AbortError` the operator read as "the server is down").
 */
export interface ApiRequestInit extends RequestInit {
  /** Abort after this many milliseconds. Omit for no client-side deadline. */
  timeoutMs?: number
  /**
   * Which same-origin API prefix to address. Omit for the mock-test API, which
   * is every caller but the staff/employee gateway.
   */
  base?: ApiBase
}

export async function apiRequest<T>(
  endpoint: string,
  init: ApiRequestInit = {},
  retryAfterRefresh = true,
): Promise<T> {
  const base: ApiBase = init.base ?? 'mocktest'

  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  if (token && shouldAttachStoredBearer(endpoint) && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  // Django's double-submit for cookie-authenticated writes. Students carry no
  // Authorization header - their session is the HttpOnly mt_tn cookie - so
  // unsafe requests must echo the csrftoken cookie in a header or the server
  // refuses them ("permission denied" on every feedback submission,
  // IELTS-335). Header-authenticated (admin) requests skip Django's CSRF
  // check entirely, so the header is attached only when it will be consulted.
  const unsafeMethod = !['GET', 'HEAD', 'OPTIONS'].includes((init.method ?? 'GET').toUpperCase())
  if (unsafeMethod && !headers.has('Authorization')) {
    const csrfToken = readCookie('csrftoken')
    if (csrfToken && !headers.has('X-CSRFToken')) headers.set('X-CSRFToken', csrfToken)
  }

  // `base` is stripped here as well as `timeoutMs`: both are ours, and passing
  // an unknown key through to `fetch` is silently ignored today but is exactly
  // the kind of thing a future runtime starts rejecting.
  const { timeoutMs, base: _base, ...requestInit } = init
  const baseUrl = base === 'staff' ? STAFF_API_BASE_URL : API_BASE_URL
  const deadline = typeof timeoutMs === 'number' && Number.isFinite(timeoutMs) && timeoutMs > 0
    ? timeoutMs
    : null
  const startedAt = Date.now()
  const controller = deadline === null ? null : new AbortController()
  let timedOut = false
  const timer = controller === null
    ? undefined
    : setTimeout(() => {
        timedOut = true
        controller.abort()
      }, deadline as number)

  try {
    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...requestInit,
      headers,
      credentials: 'include',
      ...(controller ? { signal: controller.signal } : {}),
    })

    if (response.status === 401 && retryAfterRefresh && !endpoint.includes('/auth/')) {
      const outcome = await refreshSession()
      if (outcome === 'refreshed') {
        // The deadline belongs to the LOGICAL request, not to each attempt.
        // Handing the retry a fresh `timeoutMs` let one caller-visible request
        // run for up to 2 x the deadline it asked for, so the retry inherits
        // only what is left of the budget and this attempt's timer stands down.
        const remaining = deadline === null ? null : deadline - (Date.now() - startedAt)
        if (timer !== undefined) clearTimeout(timer)
        if (remaining !== null && remaining <= 0) throw deadlineExceeded(deadline as number)
        return await apiRequest<T>(
          endpoint,
          remaining === null ? init : { ...init, timeoutMs: remaining },
          false,
        )
      }
      // Exactly one refresh was attempted, and the server refused it. Tear the
      // session down now rather than letting every caller rediscover the same
      // 401 forever. `unavailable` deliberately falls through: the request
      // fails, the session stands, and the next attempt can succeed.
      if (outcome === 'unauthenticated') announceSessionExpired()
    }

    // Parsed with the deadline still armed. Clearing the timer as soon as the
    // response HEADERS arrived meant the deadline never covered the body: a
    // server that answers promptly and then takes minutes over a large student
    // list hung the page with no timeout and no `kind: 'timeout'` error — the
    // exact operator confusion the per-endpoint deadline exists to remove.
    const envelope = await parseEnvelope<T>(response)
    // The current Django API uses { status, data, message, error } and has no
    // `success` field. Only an explicit false should turn an otherwise-2xx
    // response into a failure.
    if (!response.ok || envelope.success === false) {
      const message = errorText(envelope.error) || envelope.message || 'We could not complete your request.'
      throw new ApiError(message, response.status, 'http', undefined, envelope.data)
    }
    return envelope.data as T
  } catch (cause) {
    // Checked first, and ahead of `instanceof ApiError`: an abort during the
    // body read surfaces from `parseEnvelope` as an already-wrapped
    // `invalid-response`, which would report a healthy server as sending
    // unreadable data.
    if (timedOut) throw deadlineExceeded(deadline as number, cause)
    if (cause instanceof ApiError) throw cause
    throw new ApiError(unavailableMessage(), 0, 'network', { cause })
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * A deadline we set ourselves is not "the network is down": reporting it as one
 * sends an operator to check a healthy server. Say the request ran long.
 */
function deadlineExceeded(deadline: number, cause?: unknown): ApiError {
  return new ApiError(
    `This is taking longer than ${Math.round(deadline / 1000)} seconds. The list may be too large to load right now — try a narrower filter or a smaller page.`,
    0,
    'timeout',
    cause === undefined ? undefined : { cause },
  )
}

export const api = {
  get: <T>(endpoint: string, options: Omit<ApiRequestInit, 'method' | 'body'> = {}) =>
    apiRequest<T>(endpoint, options),
  post: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(endpoint: string, body: unknown) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) }),
}

/**
 * True when the server refused the request on identity grounds.
 *
 * 401 survives one refresh attempt before it reaches a caller, and 403 is
 * decided by who you are, not by when you asked. Neither becomes true by being
 * repeated, so retrying or polling one is pure load - which is how a single
 * browser session put ~800-900 requests an hour against five student endpoints
 * on 2026-08-23. Callers use this to stop, not to sign anyone out: a 403 can
 * also mean "this one resource is not yours", and an admin browsing a student
 * route must keep their perfectly good session.
 */
export function isAuthError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 401 || error.status === 403)
}

export function setSessionRole(role: 'student' | 'admin') {
  localStorage.setItem(ROLE_KEY, role)
}

export function clearSessionRole() {
  localStorage.removeItem(ROLE_KEY)
}

const ACTIVATION_SECRET_KEYS = ['magic_link', 'magic_link_datetime'] as const

/**
 * Drop the activation token from a serialized record before it goes anywhere.
 *
 * `Employee.magic_link` is a live, single-use account-activation token, and
 * `POST /api/employee/activate/<magic_link>` accepts it with
 * `authentication_classes([])` — so anything holding the value can take the
 * account over. `EmployeeSerializer` declares `exclude = ()`, so EVERY payload
 * built from it carries the field, including the `profile` object the admin
 * login returns (`api/views/employee_auth.py`).
 *
 * That is why this lives here rather than in the employee gateway it started
 * in: `persistSession` below writes the profile to `localStorage`, which is the
 * one place a token would persist past the tab. In practice `magic_link` is
 * `None` for any account that can complete a login — `activate_employee`
 * clears it — so this removes a near-zero exposure rather than a live one. It
 * costs one object spread, and "near zero" is not zero.
 */
export function stripActivationSecrets<T>(record: T): T {
  if (!record || typeof record !== 'object') return record
  const copy = { ...(record as Record<string, unknown>) }
  for (const key of ACTIVATION_SECRET_KEYS) delete copy[key]
  return copy as T
}

/**
 * Persist the legacy session keys shared with the Vue app and WebSocket client.
 * Django currently returns its refresh JWT as `auth_token`; retaining it under
 * both token keys preserves the existing same-origin transition behavior.
 */
export function persistSession(profile: unknown, tokens: StoredTokens) {
  // A new session gets a fresh chance to expire. Without this, signing back in
  // after an expiry would leave the latch set and the next real expiry would
  // pass unannounced.
  sessionExpiryAnnounced = false
  localStorage.setItem(AUTH_TOKEN_KEY, tokens.authToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken || tokens.authToken)
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(stripActivationSecrets(profile)))
}

/**
 * Replace the stored profile WITHOUT touching the session tokens.
 *
 * The boot path reconstructs the signed-in student from `currentUser` (there is
 * no endpoint that returns the full profile — `/auth/me/` sends a three-field
 * summary), so a profile edit that updated only React state would be undone by
 * the next reload and the student would watch their saved name revert.
 * No-ops when there is no session to update.
 */
export function updateStoredProfile(profile: unknown) {
  if (!localStorage.getItem(AUTH_TOKEN_KEY)) return
  // Same strip as persistSession: both write CURRENT_USER_KEY, so a defence on
  // only one of them is a defence with a hole in it.
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(stripActivationSecrets(profile)))
}

export function readStoredSession<T>(): T | null {
  if (!localStorage.getItem(AUTH_TOKEN_KEY)) return null

  const storedProfile = localStorage.getItem(CURRENT_USER_KEY)
  if (!storedProfile) return null

  try {
    return JSON.parse(storedProfile) as T
  } catch {
    clearStoredSession()
    return null
  }
}

export function clearStoredSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(CURRENT_USER_KEY)
  clearSessionRole()
}
